import React, { useState } from 'react';
import { ChevronDown, Wrench, ExternalLink } from 'lucide-react';
import { SEVERITY_STYLE, effortInfo, PILIERS } from '../utils/auditFindings';

/**
 * Affichage unique d'un constat d'audit (source : modèle auditFindings).
 * Un seul niveau de repli : titre + impact + action + pages concernées.
 */
function FindingItem({ finding, showPages = true }) {
  const [open, setOpen] = useState(false);
  const sev = SEVERITY_STYLE[finding.severity] || SEVERITY_STYLE.low;
  const eff = effortInfo(finding.effort);
  const pilier = PILIERS[finding.pilier]?.label || finding.pilier;
  const hasDetail = Boolean(finding.why || finding.action || (showPages && finding.pages?.length));

  return (
    <div className="rounded-lg border border-blue-gray-100 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => hasDetail && setOpen((v) => !v)}
        className={`w-full flex items-start gap-2.5 p-3 text-left transition ${hasDetail ? 'hover:bg-blue-gray-50/60 cursor-pointer' : 'cursor-default'}`}
        aria-expanded={hasDetail ? open : undefined}
      >
        <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${sev.dot}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2 flex-wrap">
            <span className="font-medium text-blue-gray-900 text-sm leading-snug">{finding.title}</span>
            <span className="text-[10px] uppercase tracking-wide text-blue-gray-400 mt-0.5">{pilier}</span>
            <span
              className="text-[10px] font-medium text-blue-gray-500 bg-blue-gray-50 border border-blue-gray-200 px-1.5 py-0.5 rounded mt-0.5"
              title={eff.hint}
            >
              <Wrench size={9} className="inline -mt-0.5 mr-0.5" />
              {eff.label}
            </span>
            {finding.count > 1 && (
              <span className="text-[11px] text-blue-gray-400 mt-0.5 whitespace-nowrap">
                {finding.count} pages
              </span>
            )}
          </div>
        </div>
        {hasDetail && (
          <ChevronDown
            size={16}
            className={`text-blue-gray-400 flex-shrink-0 mt-0.5 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {open && hasDetail && (
        <div className="px-3 pb-3 pt-0 border-t border-blue-gray-100 bg-blue-gray-50/30">
          {finding.why && (
            <p className="text-xs text-blue-gray-600 leading-relaxed mt-2">
              <span className="font-medium text-blue-gray-700">Impact : </span>
              {finding.why}
            </p>
          )}
          {finding.action && (
            <p className="text-xs text-blue-gray-700 mt-2 leading-relaxed">
              <span className="font-medium">À faire : </span>
              {finding.action}
            </p>
          )}
          {showPages && finding.pages?.length > 0 && (
            <details className="mt-3 group">
              <summary className="text-[10px] font-semibold uppercase tracking-wide text-blue-gray-400 cursor-pointer hover:text-blue-gray-600 select-none list-none flex items-center gap-1">
                <ChevronDown size={12} className="group-open:rotate-180 transition-transform" />
                Pages concernées ({finding.pages.length})
              </summary>
              <ul className="mt-1.5 space-y-0.5 max-h-32 overflow-y-auto custom-scrollbar">
                {finding.pages.map((url) => (
                  <li key={url}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-accent hover:underline flex items-center gap-1 truncate"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink size={10} className="shrink-0" />
                      <span className="truncate">{url.replace(/^https?:\/\//, '')}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Liste de constats unifiés.
 * @param {{ findings: Array, showPages?: boolean, emptyLabel?: string }} props
 */
export default function FindingsList({ findings, showPages = true, emptyLabel = 'Aucun point à signaler.' }) {
  if (!findings || findings.length === 0) {
    return <p className="text-sm text-blue-gray-500">{emptyLabel}</p>;
  }
  return (
    <div className="space-y-2">
      {findings.map((f) => (
        <FindingItem key={f.id} finding={f} showPages={showPages} />
      ))}
    </div>
  );
}
